import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField
} from "@material-ui/core";
import { Tenant } from "../../../models/Tenant";
import React, { useEffect, useState } from "react";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { Language } from "../../../models/Translation";
import useStyles from "../styles";
import Typography from "@material-ui/core/Typography";
import * as TenantApi from "./../../../api/tenantApi";
import { isOk } from "../../../result";
import { ReceiptEmailTemplate } from "../../../models/EmailTemplate";
import { toEditableTemplate, toReceiptEmailTemplates } from "./utils";
import { EditableEmailTemplate, EmailTemplateMinimal } from "./types";
import { noop } from "../../../utils";
import { useI18n } from "../../../hooks/useI18n";

interface Props {
  tenant: Tenant;
  dialogOpen: boolean;
  closeDialog: Function;
  onEditSuccess?: Function;
}

interface State {
  tenant: Tenant;
  templates: ReceiptEmailTemplate[];
  editableTemplate: EditableEmailTemplate;
  languages: ReadonlyArray<Language>;
  isLoading: boolean;
  valid: boolean;
}

const initialState: State = {
  tenant: {} as Tenant,
  templates: [],
  languages: [],
  editableTemplate: {} as EditableEmailTemplate,
  isLoading: true,
  valid: true
};

export const ReceiptEmailTemplateEditor = ({
  tenant,
  closeDialog,
  dialogOpen,
  ...props
}: Props) => {
  const classes = useStyles();
  const [state, setState] = useState(initialState);
  const { t } = useI18n();

  useEffect(() => {
    if (tenant) {
      setState({ ...state, ...initialState, isLoading: true });
      TenantApi.getReceiptEmailTemplates(tenant.id)
        .then(
          res => {
            if (isOk(res)) {
              const templates = res.value as ReceiptEmailTemplate[];
              onResponse(templates, tenant.languages);
            }
          },
          error => {
            throw error;
          }
        )
        .catch(error => {
          setState({ ...state, ...initialState, isLoading: false });
        });
    }
  }, [tenant]);

  const updateSendGridTemplateId = (
    template: EmailTemplateMinimal,
    sendGridTemplateId: string
  ) => {
    let editableTemplate = { ...state.editableTemplate };
    editableTemplate.templates = editableTemplate.templates.map(t => {
      if (t.languageCode === template.languageCode) {
        t.sendGridTemplateId = sendGridTemplateId;
      }
      return t;
    });
    setState({ ...state, editableTemplate: editableTemplate });
  };

  const updateState = (updatedField: any) => {
    let editableTemplate = { ...state.editableTemplate, ...updatedField };
    setState({ ...state, editableTemplate: editableTemplate });
  };

  const close = () => {
    closeDialog();
  };

  const isValid = () => {
    return (
      state.editableTemplate.senderEmail &&
      state.editableTemplate.senderName &&
      state.editableTemplate.description &&
      state.editableTemplate.templates.every(t => t.sendGridTemplateId)
    );
  };

  const save = () => {
    const templatesToSave = toReceiptEmailTemplates(state.editableTemplate);
    setState({ ...state, isLoading: true });

    TenantApi.saveReceiptEmailTemplates(state.tenant.id, templatesToSave)
      .then(
        res => {
          if (isOk(res)) {
            onResponse(res.value as ReceiptEmailTemplate[], state.languages);
            props.onEditSuccess ? props.onEditSuccess() : noop();
            close();
          }
        },
        error => {
          throw error;
        }
      )
      .catch(error => {
        setState({ ...state, isLoading: false });
        console.error(error);
      });
  };

  const onResponse = (
    templates: ReceiptEmailTemplate[],
    languages: ReadonlyArray<Language>
  ) => {
    const editableTemplate = toEditableTemplate(templates, tenant.languages);
    setState({
      ...state,
      tenant: tenant,
      isLoading: false,
      templates: templates,
      editableTemplate: editableTemplate,
      languages: languages
    });
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"sm"}
      open={dialogOpen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <MuiDialogTitle disableTypography>
        <Typography variant={"h6"}>
          {t("common.messages.receiptEmailTemplate")}
        </Typography>
        <IconButton
          aria-label={t("common.actions.close")}
          className={classes.closeButton}
          onClick={() => close()}
        >
          <CloseIcon />
        </IconButton>
      </MuiDialogTitle>
      <DialogContent>
        <TextField
          required
          fullWidth
          className={classes.field}
          label={t("common.formFields.description")}
          type="text"
          value={state.editableTemplate?.description || ""}
          onChange={e => updateState({ description: e.target.value as string })}
        />
        {state.editableTemplate.templates?.map(template => {
          return (
            <React.Fragment key={`${template.id}${template.languageCode}`}>
              <TextField
                required
                fullWidth
                className={classes.field}
                label={`${t("common.formFields.sendGridId")} (${
                  template.languageName
                })`}
                type="text"
                value={template.sendGridTemplateId || ""}
                onChange={e =>
                  updateSendGridTemplateId(template, e.target.value)
                }
              />
            </React.Fragment>
          );
        })}
        <TextField
          required
          fullWidth
          className={classes.field}
          label={t("common.formFields.senderEmail")}
          type="text"
          value={state.editableTemplate?.senderEmail || ""}
          onChange={e => updateState({ senderEmail: e.target.value as string })}
        />
        <TextField
          required
          fullWidth
          className={classes.field}
          label={t("common.formFields.senderName")}
          type="text"
          value={state.editableTemplate?.senderName || ""}
          onChange={e => updateState({ senderName: e.target.value as string })}
        />
        {state.isLoading && (
          <div className={classes.spinnerContainer}>
            <CircularProgress />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Box>
          <Button
            style={{ marginRight: "1rem" }}
            variant="outlined"
            color="secondary"
            onClick={() => close()}
          >
            {t("common.actions.cancel")}
          </Button>
          <Button
            variant="outlined"
            disabled={!isValid()}
            color="primary"
            onClick={() => save()}
          >
            {t("common.actions.save")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
